import Vue from 'vue'
import VueRouter from 'vue-router'

// Routes
import { canNavigate } from '@/libs/acl/routeProtection'
import { isUserLoggedIn, getUserData, getHomeRouteForLoggedInUser } from '@/auth/utils'

Vue.use(VueRouter)

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('@/views/Home.vue'),
      meta: {
        pageTitle: 'Home',
        breadcrumb: [
          {
            text: 'Home',
            active: true,
          },
        ],
        resource: 'all',
        action: 'view_home_page',
      },
    },
    {
      path: '/services',
      name: 'services',
      component: () => import('@/views/pages/services/index.vue'),
      meta: {
        pageTitle: 'Services',
        breadcrumb: [
          {
            text: 'Services',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_services',
      },
    },
    {
      path: '/latest-news',
      name: 'latest-news',
      component: () => import('@/views/pages/latestNews/index.vue'),
      meta: {
        pageTitle: 'Latest News',
        breadcrumb: [
          {
            text: 'News',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_latest_news',
      },
    },
    {
      path: '/portfolio',
      name: 'portfolio',
      component: () => import('@/views/pages/portfolio/index.vue'),
      meta: {
        pageTitle: 'Portfolio',
        breadcrumb: [
          {
            text: 'Portfolios',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_portfolios',
      },
    },
    {
      path: '/clients',
      name: 'clients',
      component: () => import('@/views/pages/clients/index.vue'),
      meta: {
        pageTitle: 'Users',
        breadcrumb: [
          {
            text: 'Clients',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_clients',
      },
    },
    {
      path: '/representatives',
      name: 'representative',
      component: () => import('@/views/pages/representatives/index.vue'),
      meta: {
        pageTitle: 'Users',
        breadcrumb: [
          {
            text: 'Representatives',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_representatives',
      },
    },
    {
      path: '/clients/:id/representatives',
      name: 'representatives',
      component: () => import('@/views/pages/clients/clientRepresentatives/index.vue'),
      meta: {
        pageTitle: 'Representatives',
        breadcrumb: [
          {
            text: 'Clients',
            active: true,
          },
          {
            text: 'Representatives',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_representatives',
      },
    },
    {
      path: '/forgot-password',
      name: 'auth-forgot-password',
      component: () => import('@/views/pages/authentication/ForgotPassword.vue'),
      meta: {
        layout: 'full',
        resource: 'Auth',
        redirectIfLoggedIn: true,
      },
    },
    {
      path: '/login',
      name: 'auth-login',
      component: () => import('@/views/Login.vue'),
      meta: {
        layout: 'full',
        resource: 'Auth',
        redirectIfLoggedIn: true,
      },
    },
    {
      path: '/register',
      name: 'auth-register',
      component: () => import('@/views/pages/authentication/Register.vue'),
      meta: {
        layout: 'full',
        resource: 'Auth',
        redirectIfLoggedIn: true,
      },
    },
    {
      path: '/error-404',
      name: 'error-404',
      component: () => import('@/views/error/Error404.vue'),
      meta: {
        layout: 'full',
      },
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/views/pages/settings/index.vue'),
      meta: {
        pageTitle: 'Settings',
        breadcrumb: [
          {
            text: 'Settings',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_settings',
      },
    },
    {
      path: '/notifications',
      name: 'notifications',
      component: () => import('@/views/pages/notifications/index.vue'),
      meta: {
        pageTitle: 'Notifications',
        breadcrumb: [
          {
            text: 'Notifications',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_notifications',
      },
    },
    {
      path: '/contact-requests',
      name: 'contact-requests',
      component: () => import('@/views/pages/contactRequests/index.vue'),
      meta: {
        pageTitle: 'Requests',
        breadcrumb: [
          {
            text: 'Contact Requests',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_contact_requests',
      },
    },
    {
      path: '/transfer-requests',
      name: 'transfer-requests',
      component: () => import('@/views/pages/transferRequests/index.vue'),
      meta: {
        pageTitle: 'Requests',
        breadcrumb: [
          {
            text: 'Transfer Requests',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_transfer_requests',
      },
    },
    {
      path: '/cancel-requests',
      name: 'cancel-requests',
      component: () => import('@/views/pages/cancelRequests/index.vue'),
      meta: {
        pageTitle: 'Requests',
        breadcrumb: [
          {
            text: 'Cancel Requests',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_cancel_contract_requests',
      },
    },
    {
      path: '/service-requests',
      name: 'service-requests',
      component: () => import('@/views/pages/serviceRequests/index.vue'),
      meta: {
        pageTitle: 'Requests',
        breadcrumb: [
          {
            text: 'Services Requests',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_service_requests',
      },
    },
    {
      path: '/register-requests',
      name: 'register-requests',
      component: () => import('@/views/pages/registerRequests/index.vue'),
      meta: {
        pageTitle: 'Requests',
        breadcrumb: [
          {
            text: 'Register Requests',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_register_requests',
      },
    },
    {
      path: '/faqs',
      name: 'faqs',
      component: () => import('@/views/pages/faqs/index.vue'),
      meta: {
        pageTitle: 'Faqs',
        breadcrumb: [
          {
            text: 'Faqs',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_faqs',
      },
    },
    {
      path: '/projects',
      name: 'projects',
      component: () => import('@/views/pages/projects/index.vue'),
      meta: {
        pageTitle: 'Projects',
        breadcrumb: [
          {
            text: 'Projects',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_projects',
      },
    },

    {
      path: '/projects/create',
      name: 'create-project',
      component: () => import('@/views/pages/projects/create.vue'),
      meta: {
        pageTitle: 'Create Project',
        breadcrumb: [
          {
            text: 'Projects',
            active: true,
          },
          {
            text: 'Create',
            active: true,
          }
        ],
        resource: 'all',
        action: 'manage_projects',
      },
    },
    {
      path: '/projects/:id/edit',
      name: 'edit-project',
      component: () => import('@/views/pages/projects/edit.vue'),
      meta: {
        pageTitle: 'Edit Project',
        breadcrumb: [
          {
            text: 'Projects',
            active: true,
          },
          {
            text: 'Edit',
            active: true,
          }
        ],
        resource: 'all',
        action: 'manage_projects',
      },

    },
    {
      path: '/projects/:id/design-phases',
      name: 'project-design-phases',
      component: () => import('@/views/pages/projects/DesignTypesPhases/index.vue'),
      meta: {
        pageTitle: 'Design Phases',
        breadcrumb: [
          {
            text: 'Projects',
            active: true,
          },
          {
            text: 'Design Phases',
            active: true,
          }
        ],
        resource: 'all',
        action: 'manage_projects',
      },
    },
    {
      path: '/projects/:id/contracts',
      name: 'project-contracts',
      component: () => import('@/views/pages/projects/contracts/index.vue'),
      meta: {
        pageTitle: 'Contracts',
        breadcrumb: [
          {
            text: 'Projects',
            active: true,
          },
          {
            text: 'Contracts',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_projects',
      },
    },
    {
      path: '/contracts/:id/invoices',
      name: 'contract-invoices',
      component: () => import('@/views/pages/projects/contracts/invoices/index.vue'),
      meta: {
        pageTitle: 'Invoices',
        breadcrumb: [
          {
            text: 'Contracts',
            active: true,
          },
          {
            text: 'Invoices',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_projects',
      },
    },
    {
      path: '/invoices/:id/payments',
      name: 'invoice-payments',
      component: () => import('@/views/pages/projects/contracts/payments/index.vue'),
      meta: {
        pageTitle: 'Payments',
        breadcrumb: [
          {
            text: 'Invoices',
            active: true,
          },
          {
            text: 'Payments',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_projects',
      },
    },
    {
      path: '/roles',
      name: 'roles',
      component: () => import('@/views/pages/roles/index.vue'),
      meta: {
        pageTitle: 'Roles',
        breadcrumb: [
          {
            text: 'Roles',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_roles',
      },
    },
    {
      path: '/admins',
      name: 'admins',
      component: () => import('@/views/pages/admins/index.vue'),
      meta: {
        pageTitle: 'Users',
        breadcrumb: [
          {
            text: 'Admins',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_admins',
      },
    },
    {
      path: '/countries',
      name: 'countries',
      component: () => import('@/views/pages/countries/index.vue'),
      meta: {
        pageTitle: 'Locations',
        breadcrumb: [
          {
            text: 'Countries',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_countries',
      },
    },
    {
      path: '/cities',
      name: 'cities',
      component: () => import('@/views/pages/cities/index.vue'),
      meta: {
        pageTitle: 'Locations',
        breadcrumb: [
          {
            text: 'Cities',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_cities',
      },
    },
    {
      path: '/areas',
      name: 'areas',
      component: () => import('@/views/pages/areas/index.vue'),
      meta: {
        pageTitle: 'Locations',
        breadcrumb: [
          {
            text: 'Areas',
            active: true,
          },
        ],
        resource: 'all',
        action: 'manage_areas',
      },
    },
    // {
    //   path: '/third-page',
    //   name: 'third-page',
    //   component: () => import('@/views/crud/index.vue'),
    //   meta: {
    //     pageTitle: 'CRUD Page',
    //     breadcrumb: [
    //       {
    //         text: 'CRUD Page',
    //         active: true,
    //       },
    //     ],
    //   },
    // },
    {
      path: '*',
      redirect: 'error-404',
    },
  ],
})

router.beforeEach((to, _, next) => {
  const isLoggedIn = isUserLoggedIn()
  if (!canNavigate(to)) {
    // Redirect to login if not logged in
    if (!isLoggedIn) return next({ name: 'auth-login' })

    // If logged in => not authorized
    return next({ name: 'misc-not-authorized' })
  }

  // Redirect if logged in
  if (to.meta.redirectIfLoggedIn && isLoggedIn) {
    const userData = getUserData()
    next(getHomeRouteForLoggedInUser(userData ? userData.role : null))
  }

  return next()
})

// ? For splash screen
// Remove afterEach hook if you are not using splash screen
router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

export default router
